import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import './GitActionsButton.css';
import GitActionsModal from './GitActionsModal';

const GitActionsButton = ({
  projectId,
  serviceName,
  repositoryUrl,
  currentBranch,
  currentTag,
  branches = [],
  tags = [],
  hasModifications = false,
  modifiedFilesCount = 0,
  commitsToPush = 0,
  commitsToPull = 0,
  pipelineStatus = null, // 'success', 'failed', 'running', 'pending', 'canceled', 'skipped'
  onBranchChange,
  onPushComplete,
  onPullComplete,
  onRefresh,
  disabled = false
}) => {
  const [showModal, setShowModal] = useState(false);
  const [showBranchDropdown, setShowBranchDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Feature flag check
  const isFeatureEnabled = true; // TODO: Replace with actual feature flag: window.FEATURE_FLAGS?.ENABLE_CONSOLIDATED_GIT_BUTTON

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowBranchDropdown(false);
      }
    };

    if (showBranchDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showBranchDropdown]);

  if (!isFeatureEnabled) {
    return null; // Fall back to old controls
  }

  // Determine visual status priority
  const getStatusInfo = () => {
    const status = {
      icon: null,
      badge: null,
      borderClass: '',
      pulse: false,
      tooltip: []
    };

    // Priority 1: Uncommitted changes (orange dot with pulse)
    if (hasModifications && modifiedFilesCount > 0) {
      status.badge = modifiedFilesCount;
      status.pulse = true;
      status.tooltip.push(`${modifiedFilesCount} uncommitted file${modifiedFilesCount > 1 ? 's' : ''}`);
    }

    // Priority 2: Commits to push (blue up arrow)
    if (commitsToPush > 0) {
      status.icon = '↑';
      status.iconClass = 'icon-push';
      status.tooltip.push(`${commitsToPush} commit${commitsToPush > 1 ? 's' : ''} to push`);
    }

    // Priority 3: Commits to pull (green down arrow)
    if (commitsToPull > 0) {
      status.icon = status.icon ? status.icon + ' ↓' : '↓';
      status.iconClass = status.iconClass ? status.iconClass + ' icon-pull' : 'icon-pull';
      status.tooltip.push(`${commitsToPull} commit${commitsToPull > 1 ? 's' : ''} to pull`);
    }

    // Background: Pipeline status (border color)
    switch (pipelineStatus) {
      case 'success':
        status.borderClass = 'border-success';
        status.tooltip.push('Last pipeline: success');
        break;
      case 'failed':
        status.borderClass = 'border-failed';
        status.tooltip.push('Last pipeline: failed');
        break;
      case 'running':
        status.borderClass = 'border-running';
        status.tooltip.push('Pipeline running...');
        break;
      case 'pending':
        status.borderClass = 'border-pending';
        status.tooltip.push('Pipeline pending...');
        break;
      case 'canceled':
        status.borderClass = 'border-canceled';
        status.tooltip.push('Pipeline canceled');
        break;
      default:
        status.borderClass = '';
    }

    return status;
  };

  const statusInfo = getStatusInfo();
  const currentRef = currentBranch || currentTag;
  const refIcon = currentBranch ? '🌿' : '🏷️';
  const tooltipText = statusInfo.tooltip.length > 0 
    ? `${currentRef}\n${statusInfo.tooltip.join('\n')}` 
    : currentRef;

  const handleButtonClick = (e) => {
    e.stopPropagation();
    setShowModal(true);
  };

  const handleDropdownClick = (e) => {
    e.stopPropagation();
    setShowBranchDropdown(!showBranchDropdown);
  };

  const handleBranchSelect = (branchOrTag, type) => {
    if (onBranchChange) {
      onBranchChange(branchOrTag, type);
    }
    setShowBranchDropdown(false);
  };

  return (
    <>
      <div className="git-actions-button-container" ref={buttonRef}>
        <button
          className={`git-actions-button ${statusInfo.borderClass} ${disabled ? 'disabled' : ''}`}
          onClick={handleButtonClick}
          disabled={disabled}
          title={tooltipText}
        >
          {/* Branch/Tag Icon and Name */}
          <span className="git-ref-icon">{refIcon}</span>
          <span className="git-ref-name">{currentRef}</span>

          {/* Status Badge (uncommitted files count) */}
          {statusInfo.badge !== null && (
            <span className={`git-status-badge ${statusInfo.pulse ? 'pulse' : ''}`}>
              ● {statusInfo.badge}
            </span>
          )}

          {/* Push/Pull Icons */}
          {statusInfo.icon && (
            <span className={`git-status-icon ${statusInfo.iconClass}`}>
              {statusInfo.icon}
            </span>
          )}
        </button>

        {/* Branch/Tag Dropdown Trigger */}
        <button
          className={`git-actions-dropdown-trigger ${statusInfo.borderClass} ${disabled ? 'disabled' : ''}`}
          onClick={handleDropdownClick}
          disabled={disabled}
          title="Switch branch or tag"
        >
          ▼
        </button>

        {/* Branch/Tag Dropdown */}
        {showBranchDropdown && (
          <div className="git-branch-dropdown" ref={dropdownRef}>
            <div className="dropdown-section">
              <div className="dropdown-header">Branches</div>
              <div className="dropdown-list">
                {branches.length > 0 ? (
                  branches.map((branch) => (
                    <button
                      key={branch}
                      className={`dropdown-item ${branch === currentBranch ? 'active' : ''}`}
                      onClick={() => handleBranchSelect(branch, 'branch')}
                    >
                      <span className="item-icon">🌿</span>
                      <span className="item-name">{branch}</span>
                      {branch === currentBranch && <span className="item-check">✓</span>}
                    </button>
                  ))
                ) : (
                  <div className="dropdown-empty">No branches available</div>
                )}
              </div>
            </div>

            {tags.length > 0 && (
              <div className="dropdown-section">
                <div className="dropdown-header">Tags</div>
                <div className="dropdown-list">
                  {tags.slice(0, 10).map((tag) => (
                    <button
                      key={tag}
                      className={`dropdown-item ${tag === currentTag ? 'active' : ''}`}
                      onClick={() => handleBranchSelect(tag, 'tag')}
                    >
                      <span className="item-icon">🏷️</span>
                      <span className="item-name">{tag}</span>
                      {tag === currentTag && <span className="item-check">✓</span>}
                    </button>
                  ))}
                  {tags.length > 10 && (
                    <div className="dropdown-footer">+{tags.length - 10} more tags</div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Git Actions Modal */}
      {showModal && (
        <GitActionsModal
          projectId={projectId}
          serviceName={serviceName}
          repositoryUrl={repositoryUrl}
          currentBranch={currentBranch}
          currentTag={currentTag}
          branches={branches}
          tags={tags}
          hasModifications={hasModifications}
          modifiedFilesCount={modifiedFilesCount}
          commitsToPush={commitsToPush}
          commitsToPull={commitsToPull}
          pipelineStatus={pipelineStatus}
          onClose={() => setShowModal(false)}
          onBranchChange={onBranchChange}
          onPushComplete={onPushComplete}
          onPullComplete={onPullComplete}
          onRefresh={onRefresh}
        />
      )}
    </>
  );
};

GitActionsButton.propTypes = {
  projectId: PropTypes.string,
  serviceName: PropTypes.string,
  repositoryUrl: PropTypes.string,
  currentBranch: PropTypes.string,
  currentTag: PropTypes.string,
  branches: PropTypes.arrayOf(PropTypes.string),
  tags: PropTypes.arrayOf(PropTypes.string),
  hasModifications: PropTypes.bool,
  modifiedFilesCount: PropTypes.number,
  commitsToPush: PropTypes.number,
  commitsToPull: PropTypes.number,
  pipelineStatus: PropTypes.oneOf(['success', 'failed', 'running', 'pending', 'canceled', 'skipped', null]),
  onBranchChange: PropTypes.func,
  onPushComplete: PropTypes.func,
  onPullComplete: PropTypes.func,
  onRefresh: PropTypes.func,
  disabled: PropTypes.bool
};

export default GitActionsButton;
